import { useLocation, Link } from 'react-router-dom';
import { LOGO_URL } from '../lib/constants';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, Mail, Phone, Globe } from 'lucide-react';

const GuestThankYou = () => {
  const location = useLocation();
  const { name, email, jurisdiction } = location.state || {};

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-center">
          <img src={LOGO_URL} alt="Privacy Pulse" className="h-8" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <Card className="border-slate-200 shadow-lg overflow-hidden">
          {/* Success Banner */}
          <div className="bg-gradient-to-r from-[#10B981] to-[#059669] p-8 text-center text-white">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-10 w-10" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Assessment Submitted!</h1>
            <p className="text-white/90">Thank you for completing your compliance assessment</p>
          </div>
          
          <CardContent className="p-8">
            {/* Confirmation Message */}
            <div className="text-center mb-8">
              {name && (
                <p className="text-lg text-[#0F172A] mb-2">
                  Thank you, <strong>{name}</strong>
                </p>
              )}
              <p className="text-[#64748B]">
                Your {jurisdiction && <strong>{jurisdiction}</strong>} Data Protection Compliance Assessment 
                has been received and is now being reviewed by our team.
              </p>
            </div>

            {/* What Happens Next */}
            <div className="bg-[#F8FAFC] rounded-lg p-6 mb-8">
              <h3 className="font-bold text-[#0F172A] mb-4">What Happens Next?</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#3B82F6]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-bold text-[#3B82F6]">1</span>
                  </div>
                  <p className="text-[#64748B]">
                    Our compliance experts will review your assessment responses
                  </p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#3B82F6]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-bold text-[#3B82F6]">2</span>
                  </div>
                  <p className="text-[#64748B]">
                    We will prepare a personalized compliance report based on your answers
                  </p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#3B82F6]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-bold text-[#3B82F6]">3</span>
                  </div>
                  <p className="text-[#64748B]">
                    A member of our team will contact you to discuss the findings and recommended next steps
                  </p>
                </li>
              </ul>
            </div>

            {/* Email Confirmation Notice */}
            {email && (
              <div className="bg-[#10B981]/5 border border-[#10B981]/20 rounded-lg p-4 mb-8">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-[#10B981]" />
                  <p className="text-sm text-[#0F172A]">
                    A confirmation email has been sent to <strong>{email}</strong>
                  </p>
                </div>
              </div>
            )}

            {/* Contact Information */}
            <div className="border-t border-slate-200 pt-6">
              <h3 className="font-bold text-[#0F172A] mb-4 text-center">Need Immediate Assistance?</h3>
              <p className="text-[#64748B] text-center mb-4">
                Contact Privicy Advisory Services:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <a 
                  href="mailto:info@myprivicy.com"
                  className="flex items-center justify-center gap-2 p-3 bg-[#F8FAFC] rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <Mail className="h-4 w-4 text-[#3B82F6]" />
                  <span className="text-sm text-[#0F172A]">info@myprivicy.com</span>
                </a>
                
                <a 
                  href="tel:+18684614572"
                  className="flex items-center justify-center gap-2 p-3 bg-[#F8FAFC] rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <Phone className="h-4 w-4 text-[#3B82F6]" />
                  <span className="text-sm text-[#0F172A]">1-868-461-4572</span>
                </a>
                
                <a 
                  href="https://www.myprivicy.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 p-3 bg-[#F8FAFC] rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <Globe className="h-4 w-4 text-[#3B82F6]" />
                  <span className="text-sm text-[#0F172A]">www.myprivicy.com</span>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-sm text-[#64748B] mt-8">
          © {new Date().getFullYear()} Privicy Advisory Services. All rights reserved.
        </p>
      </main>
    </div>
  );
};

export default GuestThankYou;
